(function () {
  "use strict";

  const CARD_SELECTOR =
    "#collection-grid .product-home-card[data-product-handle]";

  const productCache = new Map();
  const queuedCards = new WeakSet();

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function shopRoot() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return "/";
  }

  function isMobileViewport() {
    return (
      window.matchMedia &&
      window.matchMedia("(max-width: 768px)").matches
    );
  }

  function formatMoney(cents) {
    const currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : "GBP";

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
        style: "currency",
        currency: currency,
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return "£" + ((Number(cents) || 0) / 100).toFixed(2);
    }
  }

  function getProduct(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const request = fetch(
      shopRoot() + "products/" + encodeURIComponent(handle) + ".js",
      {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      }
    ).then(function (response) {
      if (!response.ok) {
        throw new Error("Unable to load product " + handle);
      }

      return response.json();
    });

    productCache.set(handle, request);
    return request;
  }

  function normalizeOptions(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];

    return (product.options || []).map(function (option, index) {
      const name =
        typeof option === "string"
          ? option
          : option && option.name
          ? option.name
          : "Option " + (index + 1);

      let values =
        option && Array.isArray(option.values) ? option.values.slice() : [];

      if (!values.length) {
        values = variants
          .map(function (variant) {
            return variant.options && variant.options[index];
          })
          .filter(function (value, valueIndex, allValues) {
            return value && allValues.indexOf(value) === valueIndex;
          });
      }

      return {
        name: name,
        values: values,
        index: index,
        type: optionType(name, values),
      };
    });
  }

  function foldOptionText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/ß/g, "ss")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  function nameIncludesAny(normalized, keys) {
    return keys.some(function (key) {
      return normalized.indexOf(key) !== -1;
    });
  }

  function inferTypeFromValues(values) {
    const list = (values || [])
      .map(function (value) {
        return String(value || "").trim();
      })
      .filter(Boolean);

    if (!list.length) {
      return "";
    }

    function matchRatio(pattern) {
      return (
        list.filter(function (value) {
          return pattern.test(value);
        }).length / list.length
      );
    }

    if (matchRatio(/^\d+\s*[ev](\s*[-–/]\s*\d+\s*[ev])?$/i) >= 0.5) {
      return "width";
    }

    if (matchRatio(/^(uk|eu|us)?\s*\d+([.,]\d+)?\s*(uk|eu|us)?$/i) >= 0.5) {
      return "size";
    }

    return "";
  }

  function optionType(name, values) {
    const normalized = foldOptionText(name);

    if (normalized === "title") {
      return "other";
    }

    if (
      nameIncludesAny(normalized, [
        "size",
        "tamano",
        "talla",
        "taglia",
        "taille",
        "grosse",
        "groesse",
        "tamanho",
        "maat",
      ])
    ) {
      return "size";
    }

    if (
      nameIncludesAny(normalized, [
        "width",
        "ancho",
        "anchura",
        "larghezza",
        "largeur",
        "breite",
        "largura",
        "breedte",
      ])
    ) {
      return "width";
    }

    if (
      nameIncludesAny(normalized, [
        "colour",
        "color",
        "colore",
        "couleur",
        "farbe",
        "kleur",
      ]) ||
      normalized === "cor"
    ) {
      return "colour";
    }

    return inferTypeFromValues(values) || "other";
  }

  function getRenderableOptions(options) {
    const groups = {
      size: [],
      width: [],
      colour: [],
    };

    options.forEach(function (option) {
      const type = option.type || optionType(option.name, option.values);

      if (groups[type]) {
        groups[type].push(option);
      }
    });

    const ordered = groups.size.concat(groups.width);

    if (!isMobileViewport()) {
      ordered.push.apply(ordered, groups.colour);
    }

    return ordered;
  }

  function swatchColour(name) {
    const value = String(name || "").toLowerCase();

    const colours = {
      navy: "#172554",
      blue: "#2563eb",
      black: "#18181b",
      white: "#f8fafc",
      cream: "#fff7dd",
      ivory: "#fffff0",
      red: "#dc2626",
      burgundy: "#7f1d1d",
      maroon: "#7f1d1d",
      green: "#3f6212",
      olive: "#65733d",
      khaki: "#a39b68",
      tan: "#b98b5f",
      brown: "#754c2e",
      beige: "#d6c4a8",
      grey: "#71717a",
      gray: "#71717a",
      silver: "#bfc3c9",
      gold: "#c79a2b",
      pink: "#ec8ca8",
      purple: "#7e22ce",
      orange: "#ea580c",
      yellow: "#eab308",
    };

    const key = Object.keys(colours).find(function (colourName) {
      return value.indexOf(colourName) !== -1;
    });

    if (key) {
      return colours[key];
    }

    let hash = 0;

    for (let i = 0; i < value.length; i += 1) {
      hash = value.charCodeAt(i) + ((hash << 5) - hash);
    }

    return "hsl(" + Math.abs(hash % 360) + " 35% 48%)";
  }

  function getImageUrl(image) {
    if (!image) return "";
    if (typeof image === "string") return image;

    return image.src || image.url || "";
  }

  function cleanImageUrl(url) {
    return String(url || "")
      .split("?")[0]
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|master)\./, ".");
  }

  function calculateDiscount(price, compareAtPrice) {
    price = Number(price) || 0;
    compareAtPrice = Number(compareAtPrice) || 0;

    if (!price || compareAtPrice <= price) {
      return 0;
    }

    return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
  }

  function updateSaleBadge(card, product, variant) {
    const badgeCorner = card.querySelector(".product-badge-corner");

    if (!badgeCorner) {
      return;
    }

    const badgeText = badgeCorner.querySelector(".badge-text");

    if (!product.available) {
      badgeCorner.hidden = false;
      badgeCorner.classList.add("wfs-v3__sale-badge");

      if (badgeText) {
        badgeText.textContent = "SOLD OUT";
      }

      return;
    }

    const price = Number(variant.price || product.price) || 0;
    const compareAtPrice = Number(variant.compare_at_price || product.compare_at_price) || 0;
    const discount = calculateDiscount(price, compareAtPrice);

    if (!discount) {
      badgeCorner.hidden = true;
      return;
    }

    badgeCorner.hidden = false;
    badgeCorner.classList.add("wfs-v3__sale-badge");

    if (badgeText) {
      badgeText.textContent = "SALE -" + discount + "%";
    }
  }

  function getAvailableValues(option, variants, selectedOptions) {
    return option.values.filter(function (value) {
      return variants.some(function (variant) {
        if (!variant.available || !variant.options) {
          return false;
        }

        return variant.options.every(function (variantValue, index) {
          if (index === option.index) {
            return variantValue === value;
          }

          return (
            selectedOptions[index] == null ||
            variantValue === selectedOptions[index]
          );
        });
      });
    });
  }

  function makeOptionMarkup(option, selectedValue, availableValues) {
    const type = option.type || optionType(option.name, option.values);

    if (type === "other" || String(option.name).toLowerCase() === "title") {
      return "";
    }

    if (!availableValues.length) {
      return "";
    }

    const label =
      type === "size"
        ? "Sizes available"
        : type === "width"
        ? "Widths available"
        : "Colours available";

    const buttons = availableValues
      .map(function (value) {
        const selected = value === selectedValue ? " is-selected" : "";
        const pressed = value === selectedValue ? "true" : "false";

        if (type === "colour") {
          return (
            '<button type="button"' +
            ' class="wfs-v3__colour-button' +
            selected +
            '"' +
            ' data-option-index="' +
            option.index +
            '"' +
            ' data-option-type="colour"' +
            ' data-option-value="' +
            escapeHtml(value) +
            '"' +
            ' aria-label="' +
            escapeHtml(value) +
            '"' +
            ' aria-pressed="' +
            pressed +
            '"' +
            ' style="--wfs-swatch:' +
            swatchColour(value) +
            '"></button>'
          );
        }

        return (
          '<button type="button"' +
          ' class="wfs-v3__option-button' +
          selected +
          '"' +
          ' data-option-index="' +
          option.index +
          '"' +
          ' data-option-type="' +
          type +
          '"' +
          ' data-option-value="' +
          escapeHtml(value) +
          '"' +
          ' aria-pressed="' +
          pressed +
          '">' +
          escapeHtml(value) +
          "</button>"
        );
      })
      .join("");

    return (
      '<div class="wfs-v3__option wfs-v3__option--' +
      type +
      '" data-option-group="' +
      option.index +
      '">' +
      '<div class="wfs-v3__option-header">' +
      '<span class="wfs-v3__option-label">' +
      label +
      "</span>" +
      (type === "colour"
        ? '<span class="wfs-v3__colour-name">' + escapeHtml(selectedValue || "") + "</span>"
        : "") +
      "</div>" +
      '<div class="wfs-v3__values">' +
      buttons +
      "</div>" +
      "</div>"
    );
  }

  function renderOptionGroups(container, options, selectedOptions, variants) {
    const optionsHost = container.querySelector(".wfs-v3__options");

    if (!optionsHost) {
      return;
    }

    function sortOptionValues(values) {
      return values.slice().sort(function (a, b) {
        return String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    }

    optionsHost.innerHTML = options
    .map(function (option) {
      const availableValues = sortOptionValues(
        getAvailableValues(option, variants, selectedOptions)
      );
      let selectedValue = selectedOptions[option.index];

      if (availableValues.indexOf(selectedValue) === -1) {
        selectedValue = availableValues[0] || "";
        selectedOptions[option.index] = selectedValue;
      }

      return makeOptionMarkup(option, selectedValue, availableValues);
    })
    .join("");
  }

  function showCardSkeleton(card) {
    if (!card || card.querySelector(".wfs-v3__skeleton")) {
      return;
    }

    const wrapper = card.querySelector(".product-home-card__wrapper");

    if (!wrapper) {
      return;
    }

    card.classList.add("wfs-v3__loading");

    const skeleton = document.createElement("div");
    skeleton.className = "wfs-v3__skeleton";
    skeleton.setAttribute("aria-hidden", "true");
    skeleton.innerHTML =
      '<div class="wfs-v3__skeleton-image"></div>' +
      '<div class="wfs-v3__skeleton-content">' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--brand"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--title"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--title-short"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--price"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--label"></div>' +
      '<div class="wfs-v3__skeleton-grid"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--label"></div>' +
      '<div class="wfs-v3__skeleton-grid wfs-v3__skeleton-grid--width"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--label"></div>' +
      '<div class="wfs-v3__skeleton-swatch"></div>' +
      '<div class="wfs-v3__skeleton-button"></div>' +
      '<div class="wfs-v3__skeleton-button wfs-v3__skeleton-button--ghost"></div>' +
      "</div>";

    wrapper.appendChild(skeleton);
  }

  function removeCardSkeleton(card) {
    if (!card) {
      return;
    }

    card.classList.remove("wfs-v3__loading");

    const skeleton = card.querySelector(".wfs-v3__skeleton");

    if (skeleton) {
      skeleton.remove();
    }
  }

  function cleanProductTitle(title, vendor) {
    let cleaned = String(title || "");

    // Remove vendor name (case-insensitive, whole words)
    if (vendor) {
      const escapedVendor = String(vendor).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      cleaned = cleaned.replace(new RegExp("\\b" + escapedVendor + "\\b", "gi"), "");
    }

    // Remove "Wide fit" / "Wide-Fit" etc.
    cleaned = cleaned.replace(/\bwide[\s-]+fit\b/gi, "");

    // Tidy leftover spaces / separators
    cleaned = cleaned
      .replace(/\s{2,}/g, " ")
      .replace(/^[\s\-–—,|/]+|[\s\-–—,|/]+$/g, "")
      .trim();

    return cleaned || String(title || "");
  }

  function buildImages(imageContainer, product, productLink, state) {
    const urls = (product.images || [])
      .map(getImageUrl)
      .filter(Boolean)
      .filter(function (url, index, allUrls) {
        return (
          allUrls.findIndex(function (otherUrl) {
            return cleanImageUrl(otherUrl) === cleanImageUrl(url);
          }) === index
        );
      });

    if (!urls.length && product.featured_image) {
      urls.push(getImageUrl(product.featured_image));
    }

    if (!urls.length) return;

    state.images = urls;

    let media = productLink.closest(".wfs-v3__media");

    if (!media) {
      media = document.createElement("div");
      media.className = "wfs-v3__media";

      productLink.parentNode.insertBefore(media, productLink);
      media.appendChild(productLink);
    }

    imageContainer.innerHTML = urls
      .map(function (url, index) {
        return (
          '<img class="wfs-v3__image' +
          (index === 0 ? " is-active" : "") +
          '" src="' +
          escapeHtml(url) +
          '" alt="' +
          escapeHtml(product.title || "Product image") +
          " - image " +
          (index + 1) +
          '"' +
          (index === 0 ? ' loading="eager"' : ' loading="lazy"') +
          ">"
        );
      })
      .join("");

    if (urls.length > 1) {
      media.insertAdjacentHTML(
        "beforeend",
        '<div class="wfs-v3__image-counter" aria-live="polite">' +
          '<span class="wfs-v3__image-current">1</span> / ' +
          urls.length +
          "</div>" +
          '<div class="wfs-v3__image-nav">' +
          '<div class="wfs-v3__image-controls">' +
          '<button type="button" class="wfs-v3__image-arrow wfs-v3__image-arrow--previous" aria-label="Previous image">&#8249;</button>' +
          '<button type="button" class="wfs-v3__image-arrow wfs-v3__image-arrow--next" aria-label="Next image">&#8250;</button>' +
          "</div>" +
          '<div class="wfs-v3__image-progress">' +
          '<div class="wfs-v3__image-progress-track">' +
          '<div class="wfs-v3__image-progress-fill"></div>' +
          "</div>" +
          "</div>" +
          "</div>"
      );
    }

    const images = imageContainer.querySelectorAll(".wfs-v3__image");
    const currentCounter = media.querySelector(".wfs-v3__image-current");
    const progressFill = media.querySelector(".wfs-v3__image-progress-fill");
    const previousButton = media.querySelector(".wfs-v3__image-arrow--previous");
    const nextButton = media.querySelector(".wfs-v3__image-arrow--next");

    state.showImage = function (index) {
      if (!images.length) return;

      if (index < 0) {
        index = images.length - 1;
      }

      if (index >= images.length) {
        index = 0;
      }

      state.currentImageIndex = index;

      images.forEach(function (image, imageIndex) {
        image.classList.toggle("is-active", imageIndex === index);
      });

      if (currentCounter) {
        currentCounter.textContent = index + 1;
      }

      if (progressFill) {
        progressFill.style.width = ((index + 1) / images.length) * 100 + "%";
      }
    };

    function changeImage(direction) {
      state.showImage(state.currentImageIndex + direction);
      state.baseImageIndex = state.currentImageIndex;
    }

    if (previousButton) {
      previousButton.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        changeImage(-1);
        event.currentTarget.blur();
      });
    }

    if (nextButton) {
      nextButton.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        changeImage(1);
        event.currentTarget.blur();
      });
    }

    state.showImage(0);
    bindImageSwipe(media, state);
  }

  function bindImageSwipe(media, state) {
    if (!media || state.images.length <= 1) {
      return;
    }

    let touchStartX = 0;
    let touchStartY = 0;

    media.addEventListener(
      "touchstart",
      function (event) {
        if (!event.touches.length) {
          return;
        }

        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      },
      { passive: true }
    );

    media.addEventListener(
      "touchend",
      function (event) {
        if (!event.changedTouches.length) {
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;

        if (Math.abs(deltaX) < 30 || Math.abs(deltaX) < Math.abs(deltaY)) {
          return;
        }

        state.showImage(state.currentImageIndex + (deltaX < 0 ? 1 : -1));
        state.baseImageIndex = state.currentImageIndex;
      },
      { passive: true }
    );
  }

  function updatePrice(body, product, variant) {
    const price = Number(variant.price || product.price) || 0;
    const compareAtPrice = Number(variant.compare_at_price || product.compare_at_price) || 0;
    const discount = calculateDiscount(price, compareAtPrice);

    const priceElement = body.querySelector(".wfs-v3__price");
    const compareElement = body.querySelector(".wfs-v3__compare-price");
    const discountElement = body.querySelector(".wfs-v3__discount");

    if (priceElement) {
      priceElement.textContent = formatMoney(price);
    }

    if (compareElement) {
      compareElement.textContent = compareAtPrice > price ? formatMoney(compareAtPrice) : "";
      compareElement.hidden = compareAtPrice <= price;
    }

    if (discountElement) {
      discountElement.textContent = discount ? "-" + discount + "%" : "";
      discountElement.hidden = !discount;
    }
  }

  function refreshCartUI(addResponse) {
    fetch(shopRoot() + "cart.js", {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then(function (response) {
        if (response && response.ok) {
          return response.json();
        }

        return null;
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            bubbles: true,
            detail: {
              product: addResponse,
              source: "wfs-product-card-v3",
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cart,
              source: "wfs-product-card-v3",
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:open", {
            bubbles: true,
          })
        );
      })
      .catch(function () {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            bubbles: true,
            detail: {
              product: addResponse,
              source: "wfs-product-card-v3",
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:open", {
            bubbles: true,
          })
        );
      });
  }

  function transformCard(card, product) {
    if (card.dataset.wfsV3 === "ready") return;

    const wrapper = card.querySelector(".product-home-card__wrapper");
    const productLink = card.querySelector(".product-home-card__image-link");
    const imageContainer = card.querySelector(".product-home-card__image");
    const oldBody = card.querySelector(".product-home-card__body");

    if (!wrapper || !productLink || !imageContainer || !oldBody) {
      removeCardSkeleton(card);
      return;
    }

    removeCardSkeleton(card);

    const variants = Array.isArray(product.variants) ? product.variants : [];

    const initialVariant =
      variants.find(function (variant) {
        return variant.available;
      }) || variants[0] || {};

    const options = getRenderableOptions(normalizeOptions(product));

    const selectedOptions = (initialVariant.options || options.map(function (option) {
      return option.values[0];
    })).slice();

    const state = {
      images: [],
      baseImageIndex: 0,
      currentImageIndex: 0,
      showImage: function () {},
    };

    buildImages(imageContainer, product, productLink, state);
    updateSaleBadge(card, product, initialVariant);

    const discount = calculateDiscount(
      initialVariant.price || product.price,
      initialVariant.compare_at_price || product.compare_at_price
    );

    const newBody = document.createElement("div");
    newBody.className = "wfs-v3__body";

    newBody.innerHTML =
      '<div class="wfs-v3__top">' +
      '<span class="wfs-v3__brand">' +
      escapeHtml(product.vendor || "") +
      "</span>" +
      "</div>" +
      '<a class="wfs-v3__title" href="' +
      escapeHtml(productLink.href) +
      '">' +
      escapeHtml(cleanProductTitle(product.title, product.vendor)) +
      "</a>" +
      '<div class="wfs-v3__price-row">' +
      '<span class="wfs-v3__price">' +
      formatMoney(initialVariant.price || product.price) +
      "</span>" +
      '<span class="wfs-v3__compare-price"' +
      (discount ? "" : " hidden") +
      ">" +
      (discount ? formatMoney(initialVariant.compare_at_price || product.compare_at_price) : "") +
      "</span>" +
      '<span class="wfs-v3__discount"' +
      (discount ? "" : " hidden") +
      ">" +
      (discount ? "-" + discount + "%" : "") +
      "</span>" +
      "</div>" +
      '<div class="wfs-v3__options"></div>' +
      '<div class="wfs-v3__actions">' +
      '<button type="button" class="wfs-v3__button wfs-v3__button--add">' +
      (product.available ? "Add to basket" : "Sold out") +
      "</button>" +
      '<a class="wfs-v3__button wfs-v3__button--details" href="' +
      escapeHtml(productLink.href) +
      '"><span class="wfs-v3__button-text wfs-v3__button-text--desktop">See more details</span><span class="wfs-v3__button-text wfs-v3__button-text--mobile">More details</span></a>' +
      "</div>";

    wrapper.appendChild(newBody);

    renderOptionGroups(newBody, options, selectedOptions, variants);

    const addButton = newBody.querySelector(".wfs-v3__button--add");

    if (!product.available && addButton) {
      addButton.disabled = true;
    }

    function findSelectedVariant() {
      return variants.find(function (variant) {
        return (
          variant.options &&
          variant.options.every(function (value, index) {
            return value === selectedOptions[index];
          })
        );
      });
    }

    function updateImageForVariant(variant) {
      const variantUrl = getImageUrl(variant && variant.featured_image);

      if (!variantUrl || !state.images.length) {
        return;
      }

      const index = state.images.findIndex(function (url) {
        return cleanImageUrl(url) === cleanImageUrl(variantUrl);
      });

      if (index !== -1) {
        state.baseImageIndex = index;
        state.showImage(index);
      }
    }

    function updateAvailability() {
      renderOptionGroups(newBody, options, selectedOptions, variants);

      const selectedVariant = findSelectedVariant();

      if (!selectedVariant || !selectedVariant.available) {
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = "Unavailable";
        }
        return;
      }

      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = "Add to basket";
      }

      updatePrice(newBody, product, selectedVariant);
      updateSaleBadge(card, product, selectedVariant);
      updateImageForVariant(selectedVariant);
    }

    newBody.addEventListener("click", function (event) {
      const control = event.target.closest("[data-option-index]");

      if (!control) {
        return;
      }

      const optionIndex = Number(control.dataset.optionIndex);
      const optionValue = control.dataset.optionValue;
      const selectedType = control.dataset.optionType;

      selectedOptions[optionIndex] = optionValue;

      newBody
        .querySelectorAll('[data-option-index="' + optionIndex + '"]')
        .forEach(function (button) {
          const selected = button.dataset.optionValue === optionValue;

          button.classList.toggle("is-selected", selected);
          button.setAttribute("aria-pressed", selected ? "true" : "false");
        });

      if (selectedType === "colour") {
        const colourName = newBody.querySelector(
          '[data-option-group="' + optionIndex + '"] .wfs-v3__colour-name'
        );

        if (colourName) {
          colourName.textContent = optionValue;
        }
      }

      updateAvailability();
    });

    addButton.addEventListener("click", function () {
      const variant = findSelectedVariant();

      if (!variant || !variant.available) {
        return;
      }

      addButton.disabled = true;
      addButton.textContent = "Adding…";

      const lineItem = {
        id: variant.id,
        quantity: 1,
      };

      const sizeProperty =
        window.theme && typeof theme.getVariantSizeProperty === "function"
          ? theme.getVariantSizeProperty(product, variant)
          : null;

      if (sizeProperty) {
        lineItem.properties = { Size: sizeProperty };
      }

      fetch(shopRoot() + "cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [lineItem],
        }),
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (data) {
              throw new Error(data.description || data.message || "Unable to add item");
            });
          }

          return response.json();
        })
        .then(function (addResponse) {
          refreshCartUI(addResponse);
          addButton.disabled = false;
          addButton.textContent = "Add to basket";
        })
        .catch(function () {
          addButton.disabled = false;
          addButton.textContent = "Add to basket";
        });
    });

    updateAvailability();
    card.dataset.wfsV3 = "ready";
  }

  const intersectionObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;

              intersectionObserver.unobserve(entry.target);

              const card = entry.target;
              const handle = card.dataset.productHandle;

              getProduct(handle)
                .then(function (product) {
                  transformCard(card, product);
                })
                .catch(function (error) {
                  console.error("[WFS Product Card V3]", error);
                  removeCardSkeleton(card);
                  card.dataset.wfsV3 = "error";
                });
            });
          },
          {
            rootMargin: "500px 0px",
          }
        )
      : null;

  function queueCard(card) {
    if (!card || queuedCards.has(card) || card.dataset.wfsV3 === "ready") {
      return;
    }

    queuedCards.add(card);
    card.dataset.wfsV3 = "queued";
    showCardSkeleton(card);

    if (intersectionObserver) {
      intersectionObserver.observe(card);
      return;
    }

    getProduct(card.dataset.productHandle)
      .then(function (product) {
        transformCard(card, product);
      })
      .catch(function (error) {
        console.error("[WFS Product Card V3]", error);
        removeCardSkeleton(card);
        card.dataset.wfsV3 = "error";
      });
  }

  function scanCards(root) {
    root = root || document;

    if (root.matches && root.matches(CARD_SELECTOR)) {
      queueCard(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(CARD_SELECTOR).forEach(queueCard);
    }
  }

  function patchWishlistButtons(root) {
    root = root || document;

    const buttons = [];

    if (root.matches && root.matches("#collection-grid .swym-button.swym-add-to-wishlist-view-product")) {
      buttons.push(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll("#collection-grid .swym-button.swym-add-to-wishlist-view-product").forEach(function (button) {
        buttons.push(button);
      });
    }

    buttons.forEach(function (button) {
      if (button.classList.contains("wfs-v3__wishlist-ready")) {
        return;
      }

      button.classList.add("wfs-v3__wishlist", "wfs-v3__wishlist-ready");
      button.style.setProperty("left", "unset", "important");
    });
  }

  function scheduleWishlistPatch() {
    patchWishlistButtons(document);
    window.setTimeout(function () {
      patchWishlistButtons(document);
    }, 100);
    window.setTimeout(function () {
      patchWishlistButtons(document);
    }, 500);
    window.setTimeout(function () {
      patchWishlistButtons(document);
    }, 1500);
  }

  function start() {
    if (!document.querySelector("#collection-grid")) {
      return;
    }

    scanCards(document);
    scheduleWishlistPatch();

    document.addEventListener("swym:collections-loaded", scheduleWishlistPatch);

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            scanCards(node);
            patchWishlistButtons(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState !== "loading") {
    start();
  } else {
    document.addEventListener("DOMContentLoaded", start);
  }
})();
